import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {EmpTreeService} from '../../../+employees/emp-tree/emp-tree.service';
import {isNumberType} from '../../../+reports/preview';
import {COMMON_SIGNS, EMPTY_SIGNS, NUMBER_SIGNS} from '../../../+reports/reports.constants';
import {FilterItem, FilterSign, SelectedTreeItem} from '../../../+reports/reports.interfaces';
import {DATE_FORMAT} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {FieldType} from '../../../shared/form-generator/fields/fields.interfaces';
import {ModalService} from '../../../shared/modal';
import {TreeNode} from '../../../shared/ui';
import {TreeItem} from '../../../shared/ui/tree-multi-select/treeview.component';
import {FieldItem} from '../../template-management.interfaces';
import {TemplateManagementService} from '../../template-management.service';

@Component({
  selector: 'gp-template-conditions-modal',
  templateUrl: './template-conditions-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateConditionsModalComponent extends Destroyable implements OnInit {

  public templateConditionsForm: UntypedFormGroup;
  public insertCondition: (InsertConditionFormData, operatorSymbol: string, fieldPath: string) => void;
  public operators = [...COMMON_SIGNS, ...EMPTY_SIGNS];
  public selectedFieldItem: FieldItem;
  public currentDate = '';
  public payValueType = FieldType.PAY_VALUE;
  public valueToCompareFieldEnabled = false;

  @Input()
  public fieldListTree: Array<TreeNode<TreeItem>>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private empTreeService: EmpTreeService,
    private templateManagementService: TemplateManagementService
  ) {
    super();
    this.templateConditionsForm = this.formBuilder.group({
      fieldName: ['', Validators.required],
      operator: ['', Validators.required],
      valueToCompare: [''],
      trueResult: ['', Validators.required],
      falseResult: ['']
    });
  }

  public ngOnInit(): void {
    this.templateConditionsForm.get('operator').valueChanges.pipe(
      tap(this.handleOperatorChange.bind(this)),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public onSubmit(): void {
    this.modalService.closeTopmostModal();
    const formValues = this.templateConditionsForm?.value;
    const operatorSymbol: string = this.operators.filter(operator => operator.value === formValues?.operator)[0]?.display;
    this.insertCondition(formValues, operatorSymbol, this.selectedFieldItem?.path);
  }

  public onFieldsSelect(field: SelectedTreeItem): void {
    this.resetForm();

    if (field.id) {
      const selectedField: TreeNode<TreeItem> = this.templateManagementService.getSelectedFieldData(this.fieldListTree, field);
      const fieldPath: string = this.templateManagementService.getPathFromReportingFields(this.fieldListTree, selectedField);

      this.handleTemplateValue({...selectedField, path: fieldPath});
    } else {
      this.setDefaultTemplateValue();
    }
  }

  public handleOperatorChange(selectedFilterSign: FilterSign): void {
    const valueToCompareControl = this.templateConditionsForm.get('valueToCompare');
    this.valueToCompareFieldEnabled = selectedFilterSign && ![FilterSign.EM, FilterSign.NEM].includes(selectedFilterSign);
    if (this.valueToCompareFieldEnabled) {
      valueToCompareControl.addValidators([Validators.required]);
    } else {
      valueToCompareControl.clearValidators();
      valueToCompareControl.reset();
    }
    valueToCompareControl.updateValueAndValidity();
  }

  private initFieldData(field: TreeNode<TreeItem>): void {
    this.selectedFieldItem = {
      name: field.data?.text,
      transId: field.data?.transIds[0],
      fieldType: <FieldType>this.getFieldType(field),
      path: field.path
    };
  }

  private handleTemplateValue(selectedField: TreeNode<TreeItem>): void {
    this.initFieldData(selectedField);

    this.operators = this.isFieldTypeNumericOrDate() ?
      [...COMMON_SIGNS, ...NUMBER_SIGNS, ...EMPTY_SIGNS] : [...COMMON_SIGNS, ...EMPTY_SIGNS];

    switch (this.selectedFieldItem.fieldType) {
      case FieldType.LIST:
        this.selectedFieldItem.metaData = this.createListTree(selectedField.data?.data?.metadatas?.items);
        break;

      case FieldType.TREE:
        this.selectedFieldItem.metaData = this.createTree(selectedField.data?.data?.metadatas?.items);
        break;

      case FieldType.ENTITY:
      case FieldType.ENTITY_DATA:
      case FieldType.ENTITY_PREFIXED:
        this.initEntityField(selectedField);
        break;

      case FieldType.LABEL:
      case FieldType.DATE:
      case FieldType.INTEGER:
      case FieldType.DECIMAL:
      case FieldType.CURRENCY:
      case FieldType.PERCENTAGE:
      case FieldType.PAY_VALUE:
        break;

      case FieldType.PEOPLE_TREE:
        break;

      default:
        break;
    }
  }

  private isFieldTypeNumericOrDate(): boolean {
    return isNumberType(this.selectedFieldItem.fieldType) || this.selectedFieldItem?.fieldType === 'DATE';
  }

  private createListTree(list: FilterItem[]): TreeNode<TreeItem>[] {
    return list.map(item => ({
      text: item.display,
      value: item.display,
      selectable: true,
      visible: true,
      children: []
    }));
  }

  private createTree(filterItems: FilterItem[]): TreeNode<TreeItem>[] {
    const lookupMap: Map<string, TreeNode<TreeItem>> = new Map<string, TreeNode<TreeItem>>();
    const parentFilterItems = filterItems.filter(item => !item.parentValue);

    parentFilterItems.forEach((item: FilterItem) => lookupMap.set(item.value, {
      text: item.display,
      value: item.value,
      selectable: item.selectable,
      visible: true,
      children: []
    }));

    const tree: Array<TreeNode<TreeItem>> = Array.from(lookupMap.values());

    parentFilterItems.forEach((parentFilterItem: FilterItem) => {
      const parentTreeNode: TreeNode<TreeItem> = tree.filter(treeItem => treeItem.value === parentFilterItem.value)[0];

      const childFilterItems: FilterItem[] = filterItems.filter(i => i.parentValue === parentFilterItem.value);

      parentTreeNode.children = childFilterItems.map(child => {
        return {
          text: child.display,
          value: child.display,
          selectable: true,
          visible: true,
          children: []
        };
      });
    });

    return tree;
  }

  private initEntityField(field: TreeNode<TreeItem>): void {
    const repEntitiesShortViewByTreeURL = field.data?.data?.metadatas?.rel;

    this.empTreeService.getRepEntitiesShortViewByTree(repEntitiesShortViewByTreeURL)
      .pipe(
        tap((result: any) => {
          const items = result.map((element: any) => {
            return <FilterItem>{
              display: element.name,
              parentValue: null,
              selectable: false,
              value: element.id,
              sign: FilterSign.EQ
            };
          }).sort(this.getSortValue);

          this.selectedFieldItem.metaData = this.createListTree(items);
        }),
        takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  private getSortValue(a: any, b: any): number {
    if (a.display > b.display) {
      return 1;
    }

    if (a.display < b.display) {
      return -1;
    }
  }

  private setDefaultTemplateValue(): void {
    this.selectedFieldItem = {
      name: 'Default Field',
      transId: 'Default Field',
      fieldType: FieldType.LABEL
    };
  }

  private getFieldType(selectedField: TreeNode<TreeItem>): string {
    return (selectedField?.data) ? selectedField?.data?.data?.types[0] : selectedField?.children[0]?.data?.data?.types[0];
  }

  private resetForm(): void {
    this.templateConditionsForm.get('valueToCompare').reset();
    this.templateConditionsForm.get('operator').reset();
    this.templateConditionsForm.get('trueResult').reset();
    this.templateConditionsForm.get('falseResult').reset();

    this.currentDate = moment().format(DATE_FORMAT);
  }
}

